/**
 * Token catalog + parse/serialize helpers backing the structured Custom
 * CSS editor in OrganizationProviderConsole.  We expose a small set of
 * the most-overridden brand tokens as color pickers; everything else
 * (font-family, one-off selectors, status overrides) goes through the
 * Extras textarea unchanged.  See STYLE_GUIDE §2 for the contract.
 */

export interface BrandToken {
    /** CSS variable name without the leading `--`. */
    name: string;
    /** Display label shown next to the color picker. */
    label: string;
    /** Default hex value (uppercase, with leading `#`). */
    defaultHex: string;
    /** One-line description, displayed as helper text under the row. */
    hint: string;
}

export const BRAND_TOKENS: ReadonlyArray<BrandToken> = [
    {
        name: 'mld-primary',
        label: 'Primary',
        defaultHex: '#42D4B0',
        hint: 'Buttons, focus rings, active selections.',
    },
    {
        name: 'mld-secondary',
        label: 'Secondary',
        defaultHex: '#FFBE7A',
        hint: 'Warm accents, "borderline" status surfaces.',
    },
    {
        name: 'mld-tertiary',
        label: 'Tertiary',
        defaultHex: '#F15E87',
        hint: 'High-severity / destructive accents.',
    },
    {
        name: 'mld-background',
        label: 'Page background',
        defaultHex: '#F0F1EB',
        hint: 'The page-level background tint.',
    },
    {
        name: 'mld-menu-background',
        label: 'Surface background',
        defaultHex: '#F9F9F7',
        hint: 'Elevated surfaces — table headers, nav strips, hover backgrounds, popovers. Pair with page background for dark themes.',
    },
    {
        name: 'mld-panel-bg',
        label: 'Card background',
        defaultHex: '#F0F1EB',
        hint: 'Card and panel surfaces. Usually matches page background.',
    },
    {
        name: 'mld-menu-text',
        label: 'Surface text',
        defaultHex: '#013B4D',
        hint: 'Text rendered on surface backgrounds (nav, menus). Must contrast with Surface background.',
    },
    {
        name: 'mld-panel-border',
        label: 'Panel border',
        defaultHex: '#BBBBAF',
        hint: 'Card / panel / modal borders. Soft warm gray by default.',
    },
    {
        name: 'mld-text-primary',
        label: 'Primary text',
        defaultHex: '#003C3C',
        hint: 'All headings and main body copy.',
    },
    {
        name: 'mld-text-secondary',
        label: 'Secondary text',
        defaultHex: '#5A7D7C',
        hint: 'Less-prominent text in card subtitles, table rows.',
    },
    {
        name: 'mld-muted-text',
        label: 'Muted text',
        defaultHex: '#77796F',
        hint: 'Captions, helper text, deemphasized labels.',
    },
    {
        name: 'mld-success',
        label: 'Success',
        defaultHex: '#059669',
        hint: 'Active / published / save-confirmation badges. Conventional green by default.',
    },
    {
        name: 'mld-warning',
        label: 'Warning',
        defaultHex: '#D97706',
        hint: 'Warning banners, "review needed", non-critical attention.',
    },
    {
        name: 'mld-danger',
        label: 'Danger',
        defaultHex: '#E11D48',
        hint: 'Destructive actions, validation errors, delete confirmations.',
    },
    {
        name: 'mld-info',
        label: 'Info',
        defaultHex: '#2563EB',
        hint: 'Informational banners, neutral callouts.',
    },
];

/** Quick lookup of token metadata by name. */
const TOKENS_BY_NAME = new Map(BRAND_TOKENS.map((t) => [t.name, t] as const));

/**
 * Surface and text tokens that auto-derive from `mld-background` when the
 * tenant overrides the page background.  Keeps the picker and the runtime
 * theme injection in lockstep — both consume `deriveSurfaceTokenHex` so a
 * designer who looks at the picker sees the same shade that will paint.
 */
const DERIVED_SURFACE_TOKENS = new Set([
    'mld-menu-background',
    'mld-panel-bg',
    'mld-menu-text',
    'mld-text-primary',
    'mld-text-secondary',
    'mld-muted-text',
    'mld-panel-border',
]);

type Rgb = [number, number, number];

function relativeLuminance([r, g, b]: Rgb): number {
    const norm = (c: number) => {
        const x = c / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

function lightenRgb([r, g, b]: Rgb, factor: number): Rgb {
    return [
        Math.min(255, Math.round(r + (255 - r) * factor)),
        Math.min(255, Math.round(g + (255 - g) * factor)),
        Math.min(255, Math.round(b + (255 - b) * factor)),
    ];
}

function darkenRgb([r, g, b]: Rgb, factor: number): Rgb {
    return [
        Math.max(0, Math.round(r * (1 - factor))),
        Math.max(0, Math.round(g * (1 - factor))),
        Math.max(0, Math.round(b * (1 - factor))),
    ];
}

function rgbToHex([r, g, b]: Rgb): string {
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToRgb(hex: string): Rgb | null {
    const triplet = hexToRgbTriplet(hex);
    if (!triplet) return null;
    const parts = triplet.split(/\s+/).map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
    return [parts[0], parts[1], parts[2]];
}

/**
 * Compute the derived RGB triplet for a surface/text token given the
 * current page-bg RGB.  Returns null for tokens that don't auto-derive.
 *
 * Light-page direction: surfaces sit slightly darker, text stays dark.
 * Dark-page direction:  surfaces sit lightly off-black, text flips to
 * light cream.  Mirrors the in-runtime derivation in useTenant.tsx so the
 * picker and the painted UI agree.
 */
export function deriveSurfaceTokenRgb(tokenName: string, pageBgRgb: Rgb): Rgb | null {
    if (!DERIVED_SURFACE_TOKENS.has(tokenName)) return null;
    const isDarkPage = relativeLuminance(pageBgRgb) < 0.5;
    switch (tokenName) {
        case 'mld-menu-background':
            return isDarkPage ? lightenRgb(pageBgRgb, 0.12) : darkenRgb(pageBgRgb, 0.025);
        case 'mld-panel-bg':
            return pageBgRgb;
        case 'mld-menu-text':
            return isDarkPage ? [240, 241, 235] : [1, 59, 77];
        case 'mld-text-primary':
            return isDarkPage ? [240, 241, 235] : [0, 60, 60];
        case 'mld-text-secondary':
            return isDarkPage ? [200, 205, 200] : [90, 125, 124];
        case 'mld-muted-text':
            return isDarkPage ? [150, 155, 145] : [119, 121, 111];
        case 'mld-panel-border':
            return isDarkPage ? lightenRgb(pageBgRgb, 0.2) : [187, 187, 175];
        default:
            return null;
    }
}

/**
 * Return the effective default hex value for a token given the *current*
 * picker state.  For surface/text tokens, when the tenant has overridden
 * `mld-background`, this returns the derived shade so the picker reflects
 * what would actually paint.  Otherwise returns the static defaultHex
 * from BRAND_TOKENS.
 */
export function effectiveDefaultHex(
    tokenName: string,
    pickerValues: Record<string, string>
): string {
    const token = TOKENS_BY_NAME.get(tokenName);
    if (!token) return '#000000';
    if (!DERIVED_SURFACE_TOKENS.has(tokenName)) return token.defaultHex;
    const pageBgHex = pickerValues['mld-background'];
    if (!pageBgHex) return token.defaultHex;
    const pageRgb = hexToRgb(pageBgHex);
    if (!pageRgb) return token.defaultHex;
    const derivedRgb = deriveSurfaceTokenRgb(tokenName, pageRgb);
    return derivedRgb ? rgbToHex(derivedRgb) : token.defaultHex;
}

/**
 * Parse a `--mld-X` declaration body that holds the R G B triplet form
 * (space-separated, no `rgb()` wrapper) and return the equivalent hex.
 * Accepts a trailing `!important` (which serializeCustomCss emits) and
 * strips it before parsing.  Returns `null` for anything we can't
 * confidently parse — caller can fall back to leaving the row at its
 * default.
 */
function rgbTripletToHex(body: string): string | null {
    const stripped = body.replace(/!important\s*$/i, '').trim();
    const parts = stripped.split(/\s+/);
    if (parts.length !== 3) return null;
    const nums = parts.map((p) => Number(p));
    if (nums.some((n) => !Number.isFinite(n) || n < 0 || n > 255)) return null;
    const [r, g, b] = nums;
    const toHex = (n: number) => Math.round(n).toString(16).padStart(2, '0').toUpperCase();
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Convert a `#RRGGBB` (or `#RGB`) hex to the R G B triplet form. */
export function hexToRgbTriplet(hex: string): string | null {
    const cleaned = hex.trim().replace(/^#/, '');
    let r: number, g: number, b: number;
    if (cleaned.length === 3) {
        r = parseInt(cleaned[0] + cleaned[0], 16);
        g = parseInt(cleaned[1] + cleaned[1], 16);
        b = parseInt(cleaned[2] + cleaned[2], 16);
    } else if (cleaned.length === 6) {
        r = parseInt(cleaned.slice(0, 2), 16);
        g = parseInt(cleaned.slice(2, 4), 16);
        b = parseInt(cleaned.slice(4, 6), 16);
    } else {
        return null;
    }
    if ([r, g, b].some((n) => Number.isNaN(n))) return null;
    return `${r} ${g} ${b}`;
}

export interface ParsedCustomCss {
    /** Hex value per known token, populated only for tokens explicitly set
     *  in the stored CSS.  Missing tokens are absent here so the picker
     *  can show its default. */
    tokenValues: Record<string, string>;
    /** CSS the editor didn't recognize — non-token rules and any non-mld
     *  variable overrides.  Rendered as-is in the Extras textarea. */
    extras: string;
}

/**
 * Parse a stored custom_css string into known-token values + the
 * leftover Extras.  The parser is deliberately conservative: it only
 * extracts `:root { --mld-X: R G B; }` declarations that match a
 * known token.  Anything else — different selectors, non-mld variables,
 * full CSS rules, comments — passes through to `extras` unchanged so
 * round-trips don't lose information.
 */
export function parseCustomCss(css: string | null | undefined): ParsedCustomCss {
    const out: ParsedCustomCss = {tokenValues: {}, extras: ''};
    if (!css) return out;

    const trimmed = css.trim();
    if (!trimmed) return out;

    // Match the first `:root { ... }` block in the CSS.  Multi-line, lazy.
    // We pull declarations out of it and leave the surrounding text intact.
    const rootMatch = trimmed.match(/:root\s*\{([\s\S]*?)\}/);
    if (!rootMatch) {
        out.extras = trimmed;
        return out;
    }

    const [fullBlock, body] = rootMatch;
    const declRegex = /(--[a-zA-Z0-9-]+)\s*:\s*([^;]+);/g;
    const unconsumed: string[] = [];

    let m: RegExpExecArray | null;
    while ((m = declRegex.exec(body)) !== null) {
        const name = m[1].replace(/^--/, '');
        const value = m[2].trim();
        const token = TOKENS_BY_NAME.get(name);
        if (!token) {
            unconsumed.push(`    ${m[1]}: ${value};`);
            continue;
        }
        const hex = rgbTripletToHex(value);
        if (!hex) {
            unconsumed.push(`    ${m[1]}: ${value};`);
            continue;
        }
        out.tokenValues[name] = hex;
    }

    // Anything outside the :root block that we already consumed becomes
    // Extras.  Re-emit unconsumed declarations inside a :root block so
    // the structure is preserved on save.
    const tail = trimmed.slice(rootMatch.index! + fullBlock.length).trim();
    const lead = trimmed.slice(0, rootMatch.index!).trim();
    const extraPieces: string[] = [];
    if (lead) extraPieces.push(lead);
    if (unconsumed.length > 0) {
        extraPieces.push(`:root {\n${unconsumed.join('\n')}\n}`);
    }
    if (tail) extraPieces.push(tail);
    out.extras = extraPieces.join('\n\n');

    return out;
}

/**
 * Build a `custom_css` string from picker values + Extras.  Tokens whose
 * picker value equals the brand default are omitted, so saving an
 * unchanged form produces an empty `:root` block (and the consumer can
 * detect that and drop the whole block).
 *
 * Each token declaration is emitted with `!important`.  This is
 * deliberate, not a hack: Tailwind 4's `@config` adapter wraps the
 * legacy tailwind.config.js color tokens into a cascade context whose
 * priority equals or beats an unlayered `:root` `<style>` block.  Naked
 * declarations consistently lose to the defaults.  `!important` says
 * exactly what the tenant override means semantically — "use this
 * value, period" — and beats the layer competition for free.
 */
export function serializeCustomCss(
    tokenValues: Record<string, string>,
    extras: string
): string {
    const lines: string[] = [];
    for (const token of BRAND_TOKENS) {
        const hex = tokenValues[token.name];
        if (!hex) continue;
        // Skip tokens equal to their *effective* default (which for
        // derived surface tokens depends on the current page-bg pick).
        // This keeps the saved CSS minimal — anything the runtime would
        // compute anyway shouldn't be persisted, so an org that nudges
        // its page bg and accepts the auto-derived surface doesn't bloat
        // their custom_css with redundant declarations.
        const effective = effectiveDefaultHex(token.name, tokenValues);
        if (hex.toUpperCase() === effective.toUpperCase()) continue;
        const triplet = hexToRgbTriplet(hex);
        if (!triplet) continue;
        lines.push(`    --${token.name}: ${triplet} !important;`);
    }

    const blocks: string[] = [];
    if (lines.length > 0) {
        blocks.push(`:root {\n${lines.join('\n')}\n}`);
    }
    const extrasTrimmed = extras.trim();
    if (extrasTrimmed) blocks.push(extrasTrimmed);

    return blocks.join('\n\n');
}
